/**
 * MemberInfoTab Component
 *
 * Edit user's own member information.
 * Features: edit member details, marital status, member type, contact info.
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy, IconX, IconAlertCircle } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  TextInput,
  Select,
  Switch,
  Grid,
  Text,
  Alert,
  Loader,
  Center,
} from '../../../components/ui';
import { toast } from '../../../components/ui';
import {
  useMemberService,
  useMemberTypeService,
  useMaritalStatusService,
} from '@actschurch/ccms-sdk';
import type { Member, MemberType, MaritalStatus } from '@actschurch/ccms-sdk';
import type { ProfileTabProps } from '../types';

export function MemberInfoTab({ userId, user, onUpdate }: ProfileTabProps) {
  const memberService = useMemberService();
  const memberTypeService = useMemberTypeService();
  const maritalStatusService = useMaritalStatusService();

  const [member, setMember] = useState<Member | null>(null);
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      maritalStatusID: 1,
      email: '',
      cellNumber: '',
      altContact: '',
      telephone: '',
      isMember: true,
      isActive: true,
      typeID: 1,
      memberCode: '',
    },
    validate: {
      firstName: (value) => {
        if (!value || value.trim() === '') return 'First name is required';
        return null;
      },
      email: (value) => {
        if (!value || value.trim() === '') return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
    },
  });

  // Gender options
  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ];

  // Prepare dropdown options
  const memberTypeOptions = useMemo(() => {
    return memberTypes.map((type) => ({
      value: String(type.typeID),
      label: type.name,
    }));
  }, [memberTypes]);

  const maritalStatusOptions = useMemo(() => {
    return maritalStatuses.map((status) => ({
      value: String(status.maritalStatusID),
      label: status.name,
    }));
  }, [maritalStatuses]);

  /**
   * Fetch member data and dropdowns
   */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all data in parallel
        const [memberData, typesData, statusesData] = await Promise.all([
          memberService.getByUserId(userId).catch(() => null),
          memberTypeService.getAll(),
          maritalStatusService.getAll(),
        ]);

        setMemberTypes(typesData);
        setMaritalStatuses(statusesData);

        if (memberData) {
          setMember(memberData);
          form.setValues({
            firstName: memberData.firstName || '',
            middleName: memberData.middleName || '',
            lastName: memberData.lastName || '',
            dateOfBirth: memberData.dateOfBirth
              ? memberData.dateOfBirth.split('T')[0]
              : '',
            gender: memberData.gender || '',
            maritalStatusID: memberData.maritalStatusID,
            email: memberData.email || '',
            cellNumber: memberData.cellNumber || '',
            altContact: memberData.altContact || '',
            telephone: memberData.telephone || '',
            isMember: memberData.isMember,
            isActive: memberData.isActive,
            typeID: memberData.typeID,
            memberCode: memberData.memberCode || '',
          });
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load member data');
        console.error('[MemberInfoTab] Error fetching member data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, memberService, memberTypeService, maritalStatusService]);

  // Track changes
  useEffect(() => {
    if (!member) return;

    const hasChanged =
      form.values.firstName !== (member.firstName || '') ||
      form.values.middleName !== (member.middleName || '') ||
      form.values.lastName !== (member.lastName || '') ||
      form.values.dateOfBirth !== (member.dateOfBirth?.split('T')[0] || '') ||
      form.values.gender !== (member.gender || '') ||
      form.values.maritalStatusID !== member.maritalStatusID ||
      form.values.email !== (member.email || '') ||
      form.values.cellNumber !== (member.cellNumber || '') ||
      form.values.altContact !== (member.altContact || '') ||
      form.values.telephone !== (member.telephone || '') ||
      form.values.isMember !== member.isMember ||
      form.values.isActive !== member.isActive ||
      form.values.typeID !== member.typeID ||
      form.values.memberCode !== (member.memberCode || '');

    setHasChanges(hasChanged);
  }, [form.values, member]);

  /**
   * Handle save changes
   */
  const handleSave = async (values: typeof form.values) => {
    if (!member) return;

    setSaving(true);
    try {
      const updateData: Partial<Member> = {
        firstName: values.firstName.trim(),
        middleName: values.middleName.trim() || null,
        lastName: values.lastName.trim() || null,
        dateOfBirth: values.dateOfBirth || null,
        gender: values.gender || null,
        maritalStatusID: values.maritalStatusID,
        email: values.email.trim(),
        cellNumber: values.cellNumber.trim() || null,
        altContact: values.altContact.trim() || null,
        telephone: values.telephone.trim() || null,
        isMember: values.isMember,
        isActive: values.isActive,
        typeID: values.typeID,
        memberCode: values.memberCode.trim() || null,
      };

      await toast.promise(
        memberService.update(member.memberID, updateData),
        {
          loading: 'Saving member information...',
          success: 'Member information updated successfully',
          error: (err: any) => err.message || 'Failed to update member information',
        }
      );

      setHasChanges(false);
      onUpdate?.();

      // Refresh member data
      const updatedMember = await memberService.getByUserId(userId);
      setMember(updatedMember);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle reset form
   */
  const handleReset = () => {
    if (!member) return;

    form.setValues({
      firstName: member.firstName || '',
      middleName: member.middleName || '',
      lastName: member.lastName || '',
      dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
      gender: member.gender || '',
      maritalStatusID: member.maritalStatusID,
      email: member.email || '',
      cellNumber: member.cellNumber || '',
      altContact: member.altContact || '',
      telephone: member.telephone || '',
      isMember: member.isMember,
      isActive: member.isActive,
      typeID: member.typeID,
      memberCode: member.memberCode || '',
    });
    setHasChanges(false);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error">
        {error}
      </Alert>
    );
  }

  if (!member) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />} title="No Member Record">
        You don't have a member record yet. Contact an administrator to create one.
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSave)}>
      <Stack gap="lg">
        {/* Personal Information Card */}
        <Card withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Personal Information
            </Text>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="First Name"
                  placeholder="John"
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Middle Name"
                  placeholder="Michael"
                  {...form.getInputProps('middleName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <TextInput
                  label="Last Name"
                  placeholder="Doe"
                  {...form.getInputProps('lastName')}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Date of Birth"
                  type="date"
                  {...form.getInputProps('dateOfBirth')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Gender"
                  placeholder="Select gender"
                  data={genderOptions}
                  clearable
                  {...form.getInputProps('gender')}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Marital Status"
                  placeholder="Select marital status"
                  data={maritalStatusOptions}
                  {...form.getInputProps('maritalStatusID')}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Contact Information Card */}
        <Card withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Contact Information
            </Text>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="john.doe@example.com"
                  required
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Cell Number"
                  placeholder="+1 234 567 8900"
                  {...form.getInputProps('cellNumber')}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Alternative Contact"
                  placeholder="+1 234 567 8901"
                  {...form.getInputProps('altContact')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Telephone"
                  placeholder="+1 234 567 8902"
                  {...form.getInputProps('telephone')}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Membership Details Card */}
        <Card withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Membership Details
            </Text>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Member Type"
                  placeholder="Select member type"
                  data={memberTypeOptions}
                  {...form.getInputProps('typeID')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Member Code"
                  placeholder="M12345"
                  {...form.getInputProps('memberCode')}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Switch
                  label="Is Member"
                  description="Toggle membership status"
                  {...form.getInputProps('isMember', { type: 'checkbox' })}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Switch
                  label="Is Active"
                  description="Toggle active status"
                  {...form.getInputProps('isActive', { type: 'checkbox' })}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Action Buttons */}
        {hasChanges && (
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconX size={16} />} onClick={handleReset}>
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              gradientVariant="primary"
            >
              Save Changes
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}
